import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { GlobalStyles } from "@mui/material";
import {
  Drawer,
  IconButton,
  Divider,
  Typography,
  Box,
  // OutlinedInput,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  TextField,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { useFormik } from "formik";
import {
  getServiceCodeSuggestions,
  getServiceNameSuggestions,
  getCategories,
} from "../../../../api/admin/service-management";
import dayjs, { Dayjs } from "dayjs";
import CreatedTimeFilter from "../../../admin-section/features/provider-management/provider-requests/provider-requests-filter-options/created-timer-filter";
import "./filter.scss";

export type FilterValues = {
  serviceName: string;
  serviceCode: string;
  categoryIds: number[];
  minPrice: string;
  maxPrice: string;
  status: Array<"active" | "inactive">;
  createdFrom?: string;
  createdTo?: string;
  needtypes: string[];
};

type FilterProps = {
  open: boolean;
  onClose: () => void;
  onApply: (values: FilterValues) => void;
  initialValues?: Partial<FilterValues>;
  onReset?: () => void;
  serviceCategoryOptions?: string[];
};

const DEFAULT_VALUES: FilterValues = {
  serviceName: "",
  serviceCode: "",
  categoryIds: [],
  minPrice: "",
  maxPrice: "",
  status: [],
  createdFrom: "",
  createdTo: "",
  needtypes: [],
};

const toSixDigits = (val: string) => val.replace(/\D/g, "").slice(0, 6);

const validatePriceRange = (minPrice: string, maxPrice: string) => {
  const minStr = String(minPrice || "").trim();
  const maxStr = String(maxPrice || "").trim();
  let minErr: string | undefined;
  let maxErr: string | undefined;
  if (!minStr) minErr = "Please enter Min base price";
  if (!maxStr) maxErr = "Please enter Max base price";
  const min = Number(minStr);
  const max = Number(maxStr);
  if (minStr && Number.isNaN(min)) minErr = "Invalid Min base price";
  if (maxStr && Number.isNaN(max)) maxErr = "Invalid Max base price";
  if (
    !minErr &&
    !maxErr &&
    Number.isFinite(min) &&
    Number.isFinite(max) &&
    min >= max
  ) {
    minErr = "Min base price should be less than Max base price";
    maxErr = "Max base price should be greater than Min base price";
  }
  return { minErr, maxErr };
};

export default function Filter({
  open,
  onClose,
  onApply,
  initialValues,
  serviceCategoryOptions,
  onReset,
}: FilterProps) {
  const categoryOptions = useMemo(
    () =>
      serviceCategoryOptions ?? [
        "Funeral Services",
        "Cremation Services",
        "Monument & Memorial",
        "Cemetery Services",
      ],
    [serviceCategoryOptions]
  );

  const [availableCategories, setAvailableCategories] = useState<
    { value: string; label: string; id: number }[]
  >([]);

  // UI-only toggles
  const [basePriceEnabled, setBasePriceEnabled] = useState(false);
  const [statusEnabled, setStatusEnabled] = useState(false);
  const [createdDateEnabled, setCreatedDateEnabled] = useState(false);

  // autosuggest state
  const [nameOptions, setNameOptions] = useState<string[]>([]);
  const [codeOptions, setCodeOptions] = useState<string[]>([]);
  const [nameLoading, setNameLoading] = useState(false);
  const [codeLoading, setCodeLoading] = useState(false);
  const [serviceNameInput, setServiceNameInput] = useState("");
  const [serviceCodeInput, setServiceCodeInput] = useState("");
  const nameDebounceRef = useRef<number | null>(null);
  const codeDebounceRef = useRef<number | null>(null);
  const [nameSearched, setNameSearched] = useState(false);
  const [codeSearched, setCodeSearched] = useState(false);

  // Date picker state
  const [createdFrom, setCreatedFrom] = useState<Dayjs | null>(null);
  const [createdTo, setCreatedTo] = useState<Dayjs | null>(null);

  // Formik validation
  const formikValidate = useCallback(
    (vals: FilterValues) => {
      const errors: Partial<Record<keyof FilterValues, string>> = {};
      // Price validation
      if (basePriceEnabled) {
        const { minErr, maxErr } = validatePriceRange(vals.minPrice, vals.maxPrice);
        if (minErr) errors.minPrice = minErr;
        if (maxErr) errors.maxPrice = maxErr;
      }
      // Status validation
      if (statusEnabled && (!vals.status || vals.status.length === 0)) {
        errors.status = "Please select at least one status.";
      }
      // Created date validation
      if (createdDateEnabled) {
        const startStr = vals.createdFrom || "";
        const endStr = vals.createdTo || "";
        if (!startStr.trim()) errors.createdFrom = "Start date is required";
        if (!endStr.trim()) errors.createdTo = "End date is required";
        if (startStr.trim() && endStr.trim()) {
          const startDate = dayjs(startStr);
          const endDate = dayjs(endStr);
          const minDate = dayjs("1900-01-01");
          const today = dayjs().endOf("day");
          // Start date validation
          if (!startDate.isValid()) {
            errors.createdFrom = "Invalid start date";
          } else if (startDate.isBefore(minDate)) {
            errors.createdFrom = "Invalid start date";
          } else if (startDate.isAfter(today)) {
            errors.createdFrom = "Start date cannot be in the future";
          }
          // End date validation
          if (!endDate.isValid()) {
            errors.createdTo = "Invalid end date";
          } else if (endDate.isBefore(minDate)) {
            errors.createdTo = "Invalid end date";
          } else if (endDate.isAfter(today)) {
            errors.createdTo = "End date cannot be in the future";
          }
          // Range validation
          if (
            startDate.isValid() &&
            endDate.isValid() &&
            startDate.isAfter(endDate)
          ) {
            errors.createdFrom = "Start date cannot be after end date";
            errors.createdTo = "End date cannot be before start date";
          }
        }
      }
      return errors;
    },
    [basePriceEnabled, statusEnabled, createdDateEnabled]
  );

  const formik = useFormik<FilterValues>({
    initialValues: DEFAULT_VALUES,
    validate: formikValidate,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (vals) => {
      const payload: FilterValues = {
        ...vals,
        minPrice: basePriceEnabled ? vals.minPrice : "",
        maxPrice: basePriceEnabled ? vals.maxPrice : "",
        status: statusEnabled ? vals.status : [],
        createdFrom: createdDateEnabled ? vals.createdFrom : "",
        createdTo: createdDateEnabled ? vals.createdTo : "",
      };
      onApply(payload);
      onClose();
    },
  });

  // Sync open/initialValues to Formik
  useEffect(() => {
    if (!open) return;
    const nextValues: FilterValues = {
      ...DEFAULT_VALUES,
      ...initialValues,
      categoryIds: initialValues?.categoryIds ?? [],
      status: initialValues?.status ?? [],
      createdFrom: initialValues?.createdFrom ?? "",
      createdTo: initialValues?.createdTo ?? "",
    };
    formik.resetForm({ values: nextValues });
    setServiceNameInput(nextValues.serviceName || "");
    setServiceCodeInput(nextValues.serviceCode || "");
    const hasBasePrice =
      String(nextValues.minPrice || "").trim() !== "" ||
      String(nextValues.maxPrice || "").trim() !== "";
    const hasStatus =
      Array.isArray(nextValues.status) && nextValues.status.length > 0;
    const hasCreatedDate =
      String(nextValues.createdFrom || "").trim() !== "" ||
      String(nextValues.createdTo || "").trim() !== "";
    setBasePriceEnabled(hasBasePrice);
    setStatusEnabled(hasStatus);
    setCreatedDateEnabled(hasCreatedDate);
    setCreatedFrom(
      nextValues.createdFrom ? dayjs(nextValues.createdFrom) : null
    );
    setCreatedTo(
      nextValues.createdTo ? dayjs(nextValues.createdTo) : null
    );
    setNameOptions([]);
    setCodeOptions([]);
    setNameSearched(false);
    setCodeSearched(false);
  }, [open, initialValues]);

  useEffect(() => {
    if (!open) return;
    const fetchCategories = async () => {
      try {
        const response = await getCategories({ type: "all" });
        if (response.status === 200) {
          const categoriesData = response?.data?.data?.data || [];
          const formattedCategories = categoriesData.map((category: any) => ({
            value: category?.name,
            label: category?.name,
            id: category?.id,
          }));
          setAvailableCategories(formattedCategories);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        setAvailableCategories([]);
      }
    };
    fetchCategories();
  }, [open]);

  const handleReset = () => {
    formik.resetForm({ values: DEFAULT_VALUES });
    setBasePriceEnabled(false);
    setStatusEnabled(false);
    setCreatedDateEnabled(false);
    setCreatedFrom(null);
    setCreatedTo(null);
    setServiceNameInput("");
    setServiceCodeInput("");
    setNameOptions([]);
    setCodeOptions([]);
    setNameSearched(false);
    setCodeSearched(false);
    onReset?.();
    // onClose();
  };

  const handleCreatedDateRange = useCallback(
    async (from: Date | null, to: Date | null) => {
      const fromDayjs = from ? dayjs(from) : null;
      const toDayjs = to ? dayjs(to) : null;

      setCreatedFrom(fromDayjs);
      setCreatedTo(toDayjs);

      if (from !== null) {
        await formik.setFieldValue(
          "createdFrom",
          fromDayjs?.toISOString() || "",
          false
        );
        formik.setFieldTouched("createdFrom", true, false);
        formik.setFieldError("createdFrom", undefined);

        await formik.validateField("createdFrom");
      }

      if (to !== null) {
        await formik.setFieldValue(
          "createdTo",
          toDayjs?.toISOString() || "",
          false
        );

        formik.setFieldTouched("createdTo", true, false);

        formik.setFieldError("createdTo", undefined);
        await formik.validateField("createdTo");
      }

    },
    [formik]
  );


  const fetchNameSuggestions = useCallback(async (text: string) => {
    setNameLoading(true);
    try {
      const res = await getServiceNameSuggestions(text);
      const services = res?.data?.data?.services || [];
      const suggestions = Array.isArray(services)
        ? services.map((s: any) => s.serviceName).filter(Boolean)
        : [];
      setNameOptions(suggestions);
    } catch (error) {
      console.error("Failed to fetch service name suggestions", error);
      setNameOptions([]);
    } finally {
      setNameLoading(false);
    }
  }, []);

  const fetchCodeSuggestions = useCallback(async (text: string) => {
    setCodeLoading(true);
    try {
      const res = await getServiceCodeSuggestions(text);
      const services = res?.data?.data?.services || [];
      const suggestions = Array.isArray(services)
        ? services.map((s: any) => s.serviceCode).filter(Boolean)
        : [];
      setCodeOptions(suggestions);
    } catch (error) {
      console.error("Failed to fetch service code suggestions", error);
      setCodeOptions([]);
    } finally {
      setCodeLoading(false);
    }
  }, []);

  // Debounce logic for name
  useEffect(() => {
    if (!open) return;
    const text = serviceNameInput.trim();
    if (text.length < 3) {
      setNameOptions([]);
      setNameSearched(false);
      if (nameDebounceRef.current) window.clearTimeout(nameDebounceRef.current);
      return;
    }
    if (nameDebounceRef.current) window.clearTimeout(nameDebounceRef.current);
    nameDebounceRef.current = window.setTimeout(() => {
      setNameSearched(true);
      fetchNameSuggestions(text);
    }, 400);
    return () => {
      if (nameDebounceRef.current) window.clearTimeout(nameDebounceRef.current);
    };
  }, [serviceNameInput, fetchNameSuggestions, open]);

  // Debounce logic for code
  useEffect(() => {
    if (!open) return;
    const text = serviceCodeInput.trim();
    if (text.length < 3) {
      setCodeOptions([]);
      setCodeSearched(false);
      if (codeDebounceRef.current) window.clearTimeout(codeDebounceRef.current);
      return;
    }
    if (codeDebounceRef.current) window.clearTimeout(codeDebounceRef.current);
    codeDebounceRef.current = window.setTimeout(() => {
      setCodeSearched(true);
      fetchCodeSuggestions(text);
    }, 400);
    return () => {
      if (codeDebounceRef.current) window.clearTimeout(codeDebounceRef.current);
    };
  }, [serviceCodeInput, fetchCodeSuggestions, open]);

  const hasAnyFilterApplied = useMemo(() => {
    const v = formik.values;
    const hasServiceName = Boolean(String(v.serviceName || "").trim());
    const hasServiceCode = Boolean(String(v.serviceCode || "").trim());
    const hasCategories = Array.isArray(v.categoryIds) && v.categoryIds.length > 0;
    const hasBasePrice = basePriceEnabled;
    const hasStatus = statusEnabled;
    const hasCreatedDate = createdDateEnabled;
    const hasNeedTypes = Array.isArray(v.needtypes) && v.needtypes.length > 0;

    return (
      hasServiceName ||
      hasServiceCode ||
      hasCategories ||
      hasBasePrice ||
      hasStatus ||
      hasCreatedDate ||
      hasNeedTypes
    );
  }, [
    formik.values.serviceName,
    formik.values.serviceCode,
    formik.values.categoryIds,
    formik.values.needtypes,
    formik.values.minPrice,
    formik.values.maxPrice,
    formik.values.status,
    formik.values.createdFrom,
    formik.values.createdTo,
    basePriceEnabled,
    statusEnabled,
    createdDateEnabled,
  ]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ className: "provider-management-filter__paper" }}
    >
      <GlobalStyles
        styles={{
          ".MuiAutocomplete-popper .MuiPaper-root": {
            padding: "0 !important",
          },
          ".MuiAutocomplete-popper .MuiAutocomplete-listbox": {
            padding: "0 !important",
            margin: "0 !important",
            maxHeight: "unset !important",
            height: "auto !important",
          },
          ".MuiAutocomplete-popper .MuiAutocomplete-noOptions": {
            padding: "6px 12px !important",
            minHeight: "unset !important",
            lineHeight: "1.2 !important",
          },
        }}
      />
      <div className="provider-management-filter">
        {/* Header */}
        <div className="filterDrawer__header">
          <Typography
            variant="h6"
            className="filterDrawer__title"
            sx={{ fontWeight: 600 }}
          >
            Filter
          </Typography>
          <IconButton
            onClick={onClose}
            className="filterDrawer__closeBtn"
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </div>
        <Divider />
        {/* Body */}
        <div className="filterDrawer__body">
          {/* Service Name */}
          <div className="filterDrawer__field">
            <Typography className="filterDrawer__label">Service Name</Typography>
            <Autocomplete
              freeSolo
              options={nameOptions}
              value={formik.values.serviceName || ""}
              inputValue={serviceNameInput}
              filterOptions={(options) =>
                options.length ? options : ["__NO_RESULTS__"]
              }
              getOptionLabel={(opt) => (opt === "__NO_RESULTS__" ? "" : opt)}
              renderOption={(props, option) => {
                if (option === "__NO_RESULTS__") {
                  return (
                    <li
                      {...props}
                      style={{ pointerEvents: "none", color: "#777" }}
                    >
                      No results found
                    </li>
                  );
                }
                return <li {...props}>{option}</li>;
              }}
              onInputChange={(_, newInputValue) => {
                setServiceNameInput(newInputValue);
                formik.setFieldValue("serviceName", newInputValue, false);
              }}
              onChange={(_, newValue) => {
                const val = (newValue ?? "") as string;
                if (val === "__NO_RESULTS__") return;
                setServiceNameInput(val);
                formik.setFieldValue("serviceName", val, false);
                setNameOptions([]);
                setNameSearched(false);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search service name"
                  className="filterDrawer__textField"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    "& .MuiInputBase-root": { height: "36px", borderRadius: "10px" },
                    "& .MuiInputAdornment-root": { mr: "6px" },
                  }}
                />
              )}
              disablePortal
            />
          </div>
          {/* Service Code */}
          <div className="filterDrawer__field">
            <Typography className="filterDrawer__label">Service Code</Typography>
            <Autocomplete
              freeSolo
              options={codeOptions}
              value={formik.values.serviceCode || ""}
              inputValue={serviceCodeInput}
              filterOptions={(options) =>
                options.length ? options : ["__NO_RESULTS__"]
              }
              getOptionLabel={(opt) => (opt === "__NO_RESULTS__" ? "" : opt)}
              renderOption={(props, option) => {
                if (option === "__NO_RESULTS__") {
                  return (
                    <li
                      {...props}
                      style={{ pointerEvents: "none", color: "#777" }}
                    >
                      No results found
                    </li>
                  );
                }
                return <li {...props}>{option}</li>;
              }}
              onInputChange={(_, newInputValue) => {
                setServiceCodeInput(newInputValue);
                formik.setFieldValue("serviceCode", newInputValue, false);
              }}
              onChange={(_, newValue) => {
                const val = (newValue ?? "") as string;
                if (val === "__NO_RESULTS__") return;
                setServiceCodeInput(val);
                formik.setFieldValue("serviceCode", val, false);
                setCodeOptions([]);
                setCodeSearched(false);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search service code"
                  className="filterDrawer__textField"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    "& .MuiInputBase-root": { height: "36px", borderRadius: "10px" },
                    "& .MuiInputAdornment-root": { mr: "6px" },
                  }}
                />
              )}
              disablePortal
            />
          </div>
          {/* Service Category */}
          <div className="filterDrawer__field">
            <Typography className="filterDrawer__label">Service Category</Typography>
            <FormControl fullWidth>
              <Select
                multiple
                displayEmpty
                value={formik.values.categoryIds}
                onChange={(e) =>
                  formik.setFieldValue("categoryIds", e.target.value as number[])
                }
                className="filterDrawer__select"
                renderValue={(selected) => {
                  const ids = selected as number[];
                  if (!ids || ids.length === 0) {
                    return (
                      <span className="filterDrawer__placeholder">
                        Select categories
                      </span>
                    );
                  }
                  const idToLabel = new Map(
                    availableCategories.map((c) => [c.id, c.label])
                  );
                  return ids
                    .map((id) => idToLabel.get(id))
                    .filter(Boolean)
                    .join(", ");
                }}
                sx={{ height: "36px", borderRadius: "10px" }}
              >
                {availableCategories.map((cat) => {
                  const checked = formik.values.categoryIds.includes(cat.id);
                  return (
                    <MenuItem
                      key={cat.id}
                      value={cat.id}
                      className="filterDrawer__menuItem"
                    >
                      <Checkbox checked={checked} />
                      <ListItemText primary={cat.label} />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </div>
          {/* Base Price toggle + fields */}
          <div className="filterDrawer__field">
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Checkbox
                checked={basePriceEnabled}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setBasePriceEnabled(checked);
                  if (!checked) {
                    formik.setFieldValue("minPrice", "", false);
                    formik.setFieldValue("maxPrice", "", false);
                    formik.setFieldTouched("minPrice", false, false);
                    formik.setFieldTouched("maxPrice", false, false);
                  }
                }}
                sx={{
                  p: 0,
                  m: 0,
                  width: 22,
                  height: 22,
                  "& .MuiSvgIcon-root": { fontSize: 22 },
                }}
              />
              <Typography className="filterDrawer__label" sx={{ mb: 0 }}>
                Base Price
              </Typography>
            </Box>
            {basePriceEnabled && (
              <Box className="filterDrawer__priceGroup">
                <div className="filterDrawer__priceRow">
                  <Typography className="filterDrawer__subLabel">Min</Typography>
                  <TextField
                    name="minPrice"
                    value={formik.values.minPrice}
                    onChange={(e) =>
                      formik.setFieldValue(
                        "minPrice",
                        toSixDigits(e.target.value)
                      )
                    }
                    onBlur={formik.handleBlur}
                    placeholder="Enter Min Value"
                    fullWidth
                    size="small"
                    className="filterDrawer__textField"
                    inputProps={{ inputMode: "numeric" }}
                    error={Boolean(
                      formik.touched.minPrice && formik.errors.minPrice
                    )}
                    helperText={
                      formik.touched.minPrice ? formik.errors.minPrice : ""
                    }
                  />
                </div>
                <div className="filterDrawer__priceRow">
                  <Typography className="filterDrawer__subLabel">Max</Typography>
                  <TextField
                    name="maxPrice"
                    value={formik.values.maxPrice}
                    onChange={(e) =>
                      formik.setFieldValue(
                        "maxPrice",
                        toSixDigits(e.target.value)
                      )
                    }
                    onBlur={formik.handleBlur}
                    placeholder="Enter Max Value"
                    fullWidth
                    size="small"
                    className="filterDrawer__textField"
                    inputProps={{ inputMode: "numeric" }}
                    error={Boolean(
                      formik.touched.maxPrice && formik.errors.maxPrice
                    )}
                    helperText={
                      formik.touched.maxPrice ? formik.errors.maxPrice : ""
                    }
                  />
                </div>
              </Box>
            )}
          </div>
          {/* Created Date toggle + component */}
          <div className="filterDrawer__field">
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Checkbox
                checked={createdDateEnabled}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setCreatedDateEnabled(checked);
                  if (!checked) {
                    setCreatedFrom(null);
                    setCreatedTo(null);
                    formik.setFieldValue("createdFrom", "", false);
                    formik.setFieldValue("createdTo", "", false);
                    formik.setFieldTouched("createdFrom", false, false);
                    formik.setFieldTouched("createdTo", false, false);
                  }
                }}
                sx={{
                  p: 0,
                  m: 0,
                  width: 22,
                  height: 22,
                  "& .MuiSvgIcon-root": { fontSize: 22 },
                }}
              />
              <Typography className="filterDrawer__label">Created Date</Typography>
            </Box>
            {createdDateEnabled && (
              <CreatedTimeFilter
                value="Custom"
                onChange={() => { }}
                isCreatedTimeEnabled={createdDateEnabled}
                onDateRangeChange={handleCreatedDateRange}
                createdTimeError={
                  formik.errors.createdFrom || formik.errors.createdTo || ""
                }
                createdTimeStartError={formik.errors.createdFrom || ""}
                createdTimeEndError={formik.errors.createdTo || ""}
                setCreatedTimeFromDate={setCreatedFrom}
                setCretedTimeToDate={setCreatedTo}
                showDropdown={false}
                fromDate={createdFrom}
                toDate={createdTo}
              />
            )}
          </div>

          {/* Need Type column */}
          <div className="filterDrawer__field">
            <Typography className="filterDrawer__label">Need Type</Typography>
            <FormControl fullWidth>
              <Select
                multiple
                displayEmpty
                value={formik.values.needtypes}
                onChange={(e) => formik.setFieldValue("needtypes", e.target.value as string[])}
                className="filterDrawer__select"
                renderValue={(selected) => {
                  const items = selected as string[];
                  if (!items || items.length === 0) {
                    return <span className="filterDrawer__placeholder">Select need types</span>;
                  }
                  return items
                    .map((val) => (val === "pre_need" ? "Pre-Need" : val === "at_need" ? "Immediate Need" : val))
                    .join(", ");
                }}
                sx={{ height: "36px", borderRadius: "10px" }}
              >
                {["pre_need", "at_need"].map((type) => (
                  <MenuItem key={type} value={type} className="filterDrawer__menuItem">
                    <Checkbox checked={formik.values.needtypes.includes(type)} />
                    <ListItemText primary={type === "pre_need" ? "Pre-Need" : "Immediate Need"} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          {/* Status toggle + checkboxes */}
          <div className="filterDrawer__field">
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Checkbox
                checked={statusEnabled}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setStatusEnabled(checked);
                  if (!checked) {
                    formik.setFieldValue("status", [], false);
                    formik.setFieldTouched("status", false, false);
                  }
                }}
                sx={{
                  p: 0,
                  m: 0,
                  width: 22,
                  height: 22,
                  "& .MuiSvgIcon-root": { fontSize: 22 },
                }}
              />
              <Typography className="filterDrawer__label">Status</Typography>
            </Box>
            {statusEnabled && (
              <>
                <div className="filterDrawer__toggleContent">
                  <div className="filterDrawer__statusRow">
                    {(["active", "inactive"] as const).map((s) => {
                      const checked = formik.values.status.includes(s);
                      return (
                        <FormControlLabel
                          key={s}
                          control={
                            <Checkbox
                              size="small"
                              checked={checked}
                              onChange={(e) => {
                                const isChecked = e.target.checked;
                                const next = isChecked
                                  ? [...formik.values.status, s]
                                  : formik.values.status.filter((x) => x !== s);
                                formik.setFieldValue("status", next);
                              }}
                            />
                          }
                          label={s === "active" ? "Active" : "Inactive"}
                        />
                      );
                    })}
                  </div>
                </div>
                {formik.errors.status && (
                  <FormHelperText error className="filterDrawer__toggleError">
                    {formik.errors.status}
                  </FormHelperText>
                )}
              </>
            )}
          </div>
        </div>
        {/* Footer */}
        {hasAnyFilterApplied && (
          <>
            <Divider />
            <div className="filterDrawer__footer">
              <Button
                variant="outlined"
                onClick={handleReset}
                className="filterDrawer__btn filterDrawer__btn--cancel"
                sx={{
                  backgroundColor: "transparent",
                  color: "black",
                  borderColor: "#d6d6d6",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.03)",
                    borderColor: "#d6d6d6",
                    boxShadow: "none",
                  },
                  "&:focus": {
                    outline: "none",
                    boxShadow: "none",
                    borderColor: "#d6d6d6",
                  },
                  "&:focus-visible": {
                    outline: "none",
                    boxShadow: "none",
                    borderColor: "#d6d6d6",
                  },
                }}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                sx={{ backgroundColor: "#EBD28D", color: "black" }}
                onClick={() => formik.handleSubmit()}
                className="filterDrawer__btn filterDrawer__btn--apply"
              >
                Apply
              </Button>
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
}